import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { orpc } from "~/lib/orpc-client";
import { useToast } from "@alchema/ui";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import { markAiText } from "~/lib/ai-output-marking";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/salary/negotiate")({
  component: NegotiateScript,
});

const CURRENCIES = ["EUR", "USD", "CHF", "GBP"] as const;

export function NegotiateScript() {
  const { toast } = useToast();

  // Form state
  const [role, setRole] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [currentOffer, setCurrentOffer] = React.useState<number | "">("");
  const [targetSalary, setTargetSalary] = React.useState<number | "">("");
  const [currency, setCurrency] = React.useState<string>("EUR");
  const [experience, setExperience] = React.useState("");

  // Script state
  const [script, setScript] = React.useState<string | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  async function handleGenerate() {
    if (!role.trim() || !company.trim() || !currentOffer || !targetSalary || !experience.trim()) {
      toast(m.salary_negotiate_error_fields(), "error");
      return;
    }
    setIsGenerating(true);
    try {
      const result = await orpc.salary.negotiate.call({
        role,
        company,
        currentOffer: Number(currentOffer),
        currency,
        experience,
        targetSalary: Number(targetSalary),
      });
      setScript(result.script);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : m.salary_negotiate_error_generate();
      toast(message, "error");
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopy() {
    if (!script) return;
    try {
      // Mark only the copied synthetic payload; the displayed script and its
      // separate visible badge remain unchanged.
      await navigator.clipboard.writeText(markAiText(script));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast(m.salary_negotiate_copy_error(), "error");
    }
  }

  const MISTAKES = [
    m.salary_negotiate_mistake_1(),
    m.salary_negotiate_mistake_2(),
    m.salary_negotiate_mistake_3(),
    m.salary_negotiate_mistake_4(),
    m.salary_negotiate_mistake_5(),
    m.salary_negotiate_mistake_6(),
    m.salary_negotiate_mistake_7(),
  ];

  const NEGOTIATE_ITEMS = [
    m.salary_negotiate_other_equity(),
    m.salary_negotiate_other_signing(),
    m.salary_negotiate_other_annual_bonus(),
    m.salary_negotiate_other_pto(),
    m.salary_negotiate_other_remote(),
    m.salary_negotiate_other_flex(),
    m.salary_negotiate_other_dev_budget(),
    m.salary_negotiate_other_relocation(),
    m.salary_negotiate_other_title_level(),
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Form + Script (2/3 width) */}
      <div className="space-y-6 lg:col-span-2">
        {/* Form */}
        <div
          className="rounded-xl p-5"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface-raised)" }}
        >
          <p className="section-label">{m.salary_negotiate_title()}</p>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
                {m.salary_negotiate_role()}
              </label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder={m.salary_negotiate_role_placeholder()}
                className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                style={{
                  background: "var(--surface-raised)",
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text)",
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
                {m.salary_negotiate_company()}
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={m.salary_negotiate_company_placeholder()}
                className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                style={{
                  background: "var(--surface-raised)",
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text)",
                }}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
                {m.salary_negotiate_current_offer()}
              </label>
              <div className="flex">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="rounded-l-lg px-2 py-2 text-xs outline-none"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-subtle)",
                    borderRight: "none",
                    color: "var(--text-muted)",
                  }}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={currentOffer}
                  onChange={(e) => setCurrentOffer(e.target.value ? Number(e.target.value) : "")}
                  placeholder="75000"
                  className="w-full rounded-r-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                  style={{
                    background: "var(--surface-raised)",
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text)",
                  }}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
                {m.salary_negotiate_target_salary()}
              </label>
              <div className="flex">
                <span
                  className="flex items-center rounded-l-lg px-2 text-xs"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-subtle)",
                    borderRight: "none",
                    color: "var(--text-muted)",
                  }}
                >
                  {currency}
                </span>
                <input
                  type="number"
                  value={targetSalary}
                  onChange={(e) => setTargetSalary(e.target.value ? Number(e.target.value) : "")}
                  placeholder="90000"
                  className="w-full rounded-r-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
                  style={{
                    background: "var(--surface-raised)",
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text)",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium" style={{ color: "var(--text-muted)" }}>
              {m.salary_negotiate_experience()}
            </label>
            <textarea
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              placeholder={m.salary_negotiate_experience_placeholder()}
              rows={3}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors focus:ring-1 focus:ring-[var(--accent)]"
              style={{
                background: "var(--surface-raised)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text)",
                resize: "vertical",
              }}
            />
          </div>

          <div className="mt-5">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="rounded-lg px-5 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: "var(--accent)", color: "#fff" }}
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {m.salary_negotiate_generating()}
                </span>
              ) : (
                m.salary_negotiate_cta()
              )}
            </button>
          </div>
        </div>

        {/* Generated Script — counter-offer display as whisper-border section */}
        {script && (
          <div
            className="rounded-xl p-5 animate-fade-up"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <div className="flex items-center justify-between">
              <p className="section-label">{m.salary_negotiate_script_title()}</p>
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text-muted)",
                    background: "transparent",
                  }}
                >
                  {copied ? m.salary_negotiate_copied() : m.salary_negotiate_copy()}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
                  style={{
                    border: "1px solid var(--border-subtle)",
                    color: "var(--text-muted)",
                    background: "transparent",
                  }}
                >
                  {m.salary_negotiate_regenerate()}
                </button>
              </div>
            </div>
            <div
              className="mt-4 rounded-lg p-4 whitespace-pre-wrap text-sm leading-relaxed"
              style={{
                border: "1px solid var(--border-subtle)",
                background: "var(--surface-raised)",
                color: "var(--text-muted)",
              }}
            >
              {script}
            </div>
            <div className="mt-3 flex justify-end">
              <AiGeneratedBadge />
            </div>
          </div>
        )}
      </div>

      {/* Tips Sidebar (1/3 width) */}
      <div className="space-y-4">
        <div
          className="rounded-xl p-5"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <p className="section-label">{m.salary_negotiate_mistakes_title()}</p>
          <ul className="mt-3 space-y-2">
            {MISTAKES.map((mistake, i) => (
              <li key={i} className="flex items-start gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                <span
                  className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: "var(--error)", opacity: 0.7 }}
                />
                {mistake}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="rounded-xl p-5"
          style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
        >
          <p className="section-label">{m.salary_negotiate_other_title()}</p>
          <ul className="mt-3 space-y-2">
            {NEGOTIATE_ITEMS.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                <input
                  type="checkbox"
                  defaultChecked={false}
                  className="h-3.5 w-3.5 rounded"
                  style={{ accentColor: "var(--accent)" }}
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
